import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, MenuItem, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Paper, Chip, IconButton, TableSortLabel,
  CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, InputAdornment, ToggleButtonGroup, ToggleButton,
  Checkbox, ListItemIcon, Divider,
} from '@mui/material';
import {
  PersonAdd, Edit, Search, Block, DeleteForever, CheckCircle, PictureAsPdf,
} from '@mui/icons-material';
import apiClient from '../api/client';
import { Employee, Client, TimesheetEntry } from '../types';
import generateTimesheetPDF from '../utils/generateTimesheetPDF';
import EmployeeFormDialog from '../components/admin/EmployeeFormDialog';

type SortField = 'name' | 'client_name' | 'hourly_rate' | 'billable_hours' | 'revenue' | 'job_title' | 'status';
type SortDir = 'asc' | 'desc';
type StatusFilter = 'all' | 'billable' | 'non_billable' | 'test' | 'deactivated';

export default function AdminEmployeesPage() {
  const now = new Date();
  const [searchParams, setSearchParams] = useSearchParams();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('billable');

  // Sorting
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Dialogs
  const [formOpen, setFormOpen] = useState(false);
  const [editEmployee, setEditEmployee] = useState<Employee | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<Employee | null>(null);
  const [deactivateDate, setDeactivateDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [deactivating, setDeactivating] = useState(false);
  const [reactivateTarget, setReactivateTarget] = useState<Employee | null>(null);
  const [reactivating, setReactivating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Employee | null>(null);
  const [deleting, setDeleting] = useState(false);

  // PDF download dialog
  const [pdfTarget, setPdfTarget] = useState<Employee | null>(null);
  const [pdfMonth, setPdfMonth] = useState(now.getMonth() + 1);
  const [pdfYear, setPdfYear] = useState(now.getFullYear());
  const [pdfLoading, setPdfLoading] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [empRes, clientRes] = await Promise.all([
        apiClient.get('/employees'),
        apiClient.get('/clients'),
      ]);
      setEmployees(empRes.data);
      setClients(clientRes.data);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle ?action=add from quick actions
  useEffect(() => {
    if (searchParams.get('action') === 'add') {
      setFormOpen(true);
      setEditEmployee(null);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    let result = [...employees];

    // Status filter
    if (statusFilter === 'all') {
      result = result.filter(e => e.status === 'active');
    } else if (statusFilter === 'billable') {
      result = result.filter(e => e.status === 'active' && !e.is_test);
    } else if (statusFilter === 'non_billable') {
      result = result.filter(e => e.status === 'active' && !e.is_test && (!e.hourly_rate || Number(e.hourly_rate) === 0));
    } else if (statusFilter === 'test') {
      result = result.filter(e => e.is_test === true);
    } else if (statusFilter === 'deactivated') {
      result = result.filter(e => e.status === 'inactive');
    }

    // Client filter
    if (clientFilter.length > 0) {
      result = result.filter(e => clientFilter.includes(e.client_id));
    }

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(e =>
        e.name.toLowerCase().includes(q) ||
        e.uid.toLowerCase().includes(q) ||
        e.email.toLowerCase().includes(q) ||
        (e.client_name && e.client_name.toLowerCase().includes(q))
      );
    }

    // Sort
    result.sort((a, b) => {
      let cmp: number;
      if (sortField === 'revenue') {
        const aRev = (Number(a.hourly_rate) || 0) * (Number(a.billable_hours) || 0);
        const bRev = (Number(b.hourly_rate) || 0) * (Number(b.billable_hours) || 0);
        cmp = aRev - bRev;
      } else if (['hourly_rate', 'billable_hours'].includes(sortField)) {
        cmp = (Number(a[sortField]) || 0) - (Number(b[sortField]) || 0);
      } else {
        const aVal = (a[sortField] ?? '').toString().toLowerCase();
        const bVal = (b[sortField] ?? '').toString().toLowerCase();
        cmp = aVal.localeCompare(bVal, undefined, { numeric: true });
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [employees, search, clientFilter, statusFilter, sortField, sortDir]);

  const handleOpenAdd = () => {
    setEditEmployee(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (emp: Employee) => {
    setEditEmployee(emp);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditEmployee(null);
  };

  const handleSaved = () => {
    setSnackbar({ message: editEmployee ? 'Employee updated' : 'Employee created', severity: 'success' });
    fetchData();
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    try {
      await apiClient.delete(`/employees/${deactivateTarget.id}`, { data: { deactivated_date: deactivateDate } });
      setSnackbar({ message: `${deactivateTarget.name} deactivated`, severity: 'success' });
      setDeactivateTarget(null);
      fetchData();
    } catch (err: any) {
      setSnackbar({ message: err.response?.data?.error || 'Failed to deactivate', severity: 'error' });
    } finally {
      setDeactivating(false);
    }
  };

  const handleReactivate = async () => {
    if (!reactivateTarget) return;
    setReactivating(true);
    try {
      await apiClient.post(`/employees/${reactivateTarget.id}/reactivate`);
      setSnackbar({ message: `${reactivateTarget.name} reactivated`, severity: 'success' });
      setReactivateTarget(null);
      fetchData();
    } catch (err: any) {
      setSnackbar({ message: err.response?.data?.error || 'Failed to reactivate', severity: 'error' });
    } finally {
      setReactivating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/employees/${deleteTarget.id}/permanent`);
      setSnackbar({ message: `${deleteTarget.name} permanently deleted`, severity: 'success' });
      setDeleteTarget(null);
      fetchData();
    } catch (err: any) {
      setSnackbar({ message: err.response?.data?.error || 'Failed to delete employee', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  // Download PDF for an employee for a specific month/year
  const handleDownloadEmployeePDF = async () => {
    if (!pdfTarget) return;
    setPdfLoading(true);
    try {
      // Fetch timesheets for the month/year and find the one for this employee
      const { data: timesheets } = await apiClient.get('/timesheets', { params: { month: pdfMonth, year: pdfYear, include_test: true } });
      const ts = timesheets.find((t: any) => t.employee_id === pdfTarget.id);
      if (!ts) {
        setSnackbar({ message: `No timesheet found for ${pdfTarget.name} in the selected month`, severity: 'error' });
        setPdfLoading(false);
        return;
      }
      // Fetch entries
      const { data: entriesData } = await apiClient.get(`/timesheets/${ts.id}/entries`);
      generateTimesheetPDF({
        employeeName: ts.employee_name,
        clientName: ts.client_name,
        jobTitle: ts.job_title,
        month: ts.month,
        year: ts.year,
        status: ts.status,
        expectedDailyHours: Number(ts.expected_daily_hours || 8),
        totalHours: Number(ts.total_hours || 0),
        entries: entriesData as TimesheetEntry[],
        isIndirect: ts.is_indirect,
      });
      setPdfTarget(null);
    } catch (err: any) {
      setSnackbar({ message: err.response?.data?.error || 'Failed to generate PDF', severity: 'error' });
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  const AVATAR_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#EC4899', '#06B6D4', '#F97316'];
  const getAvatarColor = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  };
  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const getStatusChip = (emp: Employee) => {
    if (emp.is_test) return <Chip label="Test" size="small" sx={{ backgroundColor: '#FEF3C7', color: '#92400E', fontWeight: 500, fontSize: 11, height: 24 }} />;
    if (emp.status === 'active') return <Chip label="Billable" size="small" sx={{ backgroundColor: '#D1FAE5', color: '#065F46', fontWeight: 500, fontSize: 11, height: 24 }} />;
    // Deactivated
    const dateLabel = emp.deactivated_date
      ? new Date(emp.deactivated_date.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
      : null;
    return (
      <Chip
        label={dateLabel ? `Deactivated ${dateLabel}` : 'Deactivated'}
        size="small"
        sx={{ backgroundColor: '#FEE2E2', color: '#991B1B', fontWeight: 500, fontSize: 11, height: 24 }}
      />
    );
  };

  const getTypeChip = (emp: Employee) => {
    if (emp.is_indirect) return <Chip label="Indirect" size="small" sx={{ backgroundColor: '#F3E8FF', color: '#7E22CE', fontWeight: 500, fontSize: 11, height: 24 }} />;
    return <Chip label="Direct" size="small" sx={{ backgroundColor: '#EFF6FF', color: '#1D4ED8', fontWeight: 500, fontSize: 11, height: 24 }} />;
  };

  const SortHeader = ({ field, label, align, width }: { field: SortField; label: string; align?: 'left' | 'right' | 'center'; width?: string }) => (
    <TableCell sx={{ fontWeight: 600, width }} align={align}>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDir : 'asc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: '-0.02em' }}>Employee Management</Typography>
          <Typography sx={{ fontSize: 13, color: '#94A3B8', mt: 0.25 }}>View and manage all your team members in one place</Typography>
        </Box>
        <Button variant="contained" startIcon={<PersonAdd />} onClick={handleOpenAdd}
          sx={{ borderRadius: '10px', px: 2.5, py: 1 }}>
          Add New Employee
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search by name, ID, or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: 280 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#94A3B8' }} /></InputAdornment>,
          }}
        />
        <TextField
          select
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value as unknown as string[])}
          size="small"
          sx={{ minWidth: 180 }}
          label="Client"
          SelectProps={{
            multiple: true,
            renderValue: (selected) => {
              const sel = selected as string[];
              if (sel.length === 0) return 'All Clients';
              if (sel.length === 1) return clients.find(c => c.id === sel[0])?.client_name ?? sel[0];
              return `${sel.length} clients`;
            },
          }}
        >
          {(() => {
            const activeIds = clients.filter(c => c.status === 'active').map(c => c.id);
            return [
              <MenuItem
                key="__select_all__"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setClientFilter(clientFilter.length === activeIds.length ? [] : activeIds);
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Checkbox
                    checked={activeIds.length > 0 && clientFilter.length === activeIds.length}
                    indeterminate={clientFilter.length > 0 && clientFilter.length < activeIds.length}
                    size="small"
                  />
                </ListItemIcon>
                Select All
              </MenuItem>,
              <Divider key="__divider__" />,
              ...clients.filter(c => c.status === 'active').map(c => (
                <MenuItem key={c.id} value={c.id}>
                  <ListItemIcon sx={{ minWidth: 32 }}><Checkbox checked={clientFilter.includes(c.id)} size="small" /></ListItemIcon>
                  {c.client_name}
                </MenuItem>
              )),
            ];
          })()}
        </TextField>
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(_, v) => v && setStatusFilter(v)}
          size="small"
        >
          <ToggleButton value="all" sx={{ textTransform: 'none', px: 2 }}>All</ToggleButton>
          <ToggleButton value="billable" sx={{ textTransform: 'none', px: 2 }}>Billable</ToggleButton>
          <ToggleButton value="non_billable" sx={{ textTransform: 'none', px: 2 }}>Non Billable</ToggleButton>
          <ToggleButton value="test" sx={{ textTransform: 'none', px: 2 }}>Test</ToggleButton>
          <ToggleButton value="deactivated" sx={{ textTransform: 'none', px: 2 }}>Deactivated</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Table */}
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#0F172A' }}>
            All Employees
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
            {filtered.length} employee{filtered.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                <SortHeader field="name" label="Name" width="17%" />
                <SortHeader field="job_title" label="Job Title" width="12%" />
                <SortHeader field="client_name" label="Client" width="10%" />
                <SortHeader field="hourly_rate" label="Client Rate ($/hr)" align="right" width="8%" />
                <TableCell align="right" sx={{ fontWeight: 600, width: '8%' }}>Kovil Cost ($/hr)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, width: '10%' }}>Gross Margin ($/hr)</TableCell>
                <SortHeader field="billable_hours" label="Billable Hrs" align="right" width="7%" />
                <SortHeader field="revenue" label="Revenue (MTD)" align="right" width="9%" />
                <SortHeader field="status" label="Status" width="8%" />
                <TableCell sx={{ fontWeight: 600, width: '5%' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '6%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    {employees.length === 0 ? 'No employees yet' : 'No employees match filters'}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((emp) => {
                  const color = getAvatarColor(emp.name);
                  return (
                    <TableRow
                      key={emp.id}
                      hover
                      sx={{ '&:last-child td': { borderBottom: 0 } }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{
                            width: 36, height: 36, borderRadius: '50%',
                            backgroundColor: `${color}15`, color: color,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 13, fontWeight: 700, flexShrink: 0,
                          }}>
                            {getInitials(emp.name)}
                          </Box>
                          <Box>
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#0F172A', fontSize: 13 }}>
                              {emp.name}
                            </Typography>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#94A3B8', fontSize: 11 }}>
                              {emp.uid}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontSize={13}>{emp.job_title || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontSize={13}>{emp.client_name}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontSize={13} color="text.secondary">
                          ${Number(emp.hourly_rate) || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontSize={13} color="text.secondary">
                          {emp.kovil_cost != null ? `$${Number(emp.kovil_cost)}` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {(() => {
                          const rate = Number(emp.hourly_rate) || 0;
                          const cost = Number(emp.kovil_cost) || 0;
                          if (!emp.hourly_rate && !emp.kovil_cost) return <Typography variant="body2" fontSize={13} color="text.secondary">—</Typography>;
                          const margin = rate - cost;
                          const pct = rate > 0 ? Math.round((margin / rate) * 100) : null;
                          return (
                            <Typography variant="body2" fontSize={13} fontWeight={600}
                              sx={{ color: margin >= 0 ? '#15803D' : '#DC2626' }}>
                              ${margin.toFixed(2)}{pct !== null ? ` (${pct}%)` : ''}
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontSize={13}>
                          {emp.is_test ? 0 : (Number(emp.billable_hours) || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontSize={13} fontWeight={600} sx={{ color: '#0F172A' }}>
                          ${emp.is_test ? '0' : ((Number(emp.hourly_rate) || 0) * (Number(emp.billable_hours) || 0)).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getStatusChip(emp)}
                      </TableCell>
                      <TableCell>
                        {getTypeChip(emp)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.25 }}>
                          <IconButton size="small" onClick={() => { setPdfTarget(emp); setPdfMonth(now.getMonth() + 1); setPdfYear(now.getFullYear()); }} title="Download PDF"
                            sx={{ color: '#94A3B8', '&:hover': { color: '#059669', backgroundColor: '#D1FAE5' } }}>
                            <PictureAsPdf sx={{ fontSize: 18 }} />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleOpenEdit(emp)} title="Edit"
                            sx={{ color: '#94A3B8', '&:hover': { color: '#3B82F6', backgroundColor: '#EFF6FF' } }}>
                            <Edit sx={{ fontSize: 18 }} />
                          </IconButton>
                          {emp.status === 'active' ? (
                            <IconButton
                              size="small"
                              onClick={() => { setDeactivateTarget(emp); setDeactivateDate(new Date().toISOString().slice(0, 10)); }}
                              title="Deactivate"
                              sx={{ color: '#94A3B8', '&:hover': { color: '#F59E0B', backgroundColor: '#FEF3C7' } }}
                            >
                              <Block sx={{ fontSize: 18 }} />
                            </IconButton>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => setReactivateTarget(emp)}
                              title="Activate"
                              sx={{ color: '#94A3B8', '&:hover': { color: '#10B981', backgroundColor: '#D1FAE5' } }}
                            >
                              <CheckCircle sx={{ fontSize: 18 }} />
                            </IconButton>
                          )}
                          <IconButton size="small" onClick={() => setDeleteTarget(emp)} title="Delete permanently"
                            sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444', backgroundColor: '#FEE2E2' } }}>
                            <DeleteForever sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <EmployeeFormDialog
        open={formOpen}
        onClose={handleFormClose}
        onSaved={handleSaved}
        employee={editEmployee}
      />

      {/* Deactivate Dialog */}
      <Dialog open={!!deactivateTarget} onClose={() => setDeactivateTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>Deactivate Employee</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Deactivating <strong>{deactivateTarget?.name}</strong> will disable their login and hide them from active reports.
          </DialogContentText>
          <TextField
            label="Effective Date"
            type="date"
            value={deactivateDate}
            onChange={(e) => setDeactivateDate(e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            helperText="Defaults to today. You can backdate if needed."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeactivateTarget(null)} color="inherit" disabled={deactivating}>Cancel</Button>
          <Button onClick={handleDeactivate} color="warning" variant="contained" disabled={deactivating}>
            {deactivating ? 'Deactivating...' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activate Dialog */}
      <Dialog open={!!reactivateTarget} onClose={() => setReactivateTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>Activate Employee</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            <strong>{reactivateTarget?.name}</strong> will be reactivated immediately. Their login will be re-enabled and timesheets will be generated from this month onwards.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setReactivateTarget(null)} color="inherit" disabled={reactivating}>Cancel</Button>
          <Button onClick={handleReactivate} color="success" variant="contained" disabled={reactivating}>
            {reactivating ? 'Activating...' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1.5, color: 'error.main' }}>Permanently Delete Employee</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to permanently delete <strong>{deleteTarget?.name}</strong>?
            This will remove the employee, their user account, and all associated timesheets. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteTarget(null)} color="inherit" disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Download Dialog */}
      <Dialog open={!!pdfTarget} onClose={() => !pdfLoading && setPdfTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>Download Timesheet PDF</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2.5 }}>
            Select a month to download the PDF for <strong>{pdfTarget?.name}</strong>.
          </DialogContentText>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              select
              label="Month"
              value={pdfMonth}
              onChange={(e) => setPdfMonth(Number(e.target.value))}
              size="small"
              sx={{ flex: 1 }}
            >
              {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Year"
              value={pdfYear}
              onChange={(e) => setPdfYear(Number(e.target.value))}
              size="small"
              sx={{ width: 100 }}
            >
              {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPdfTarget(null)} color="inherit" disabled={pdfLoading}>Cancel</Button>
          <Button onClick={handleDownloadEmployeePDF} variant="contained" disabled={pdfLoading}
            startIcon={<PictureAsPdf />}>
            {pdfLoading ? 'Generating...' : 'Download PDF'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
